import { Box, Chip, Switch, Tooltip, Typography } from '@shared/ui';
import type React from 'react';
import type { ExternalCalendar } from './types';

interface CalendarRowProps {
  calendar: ExternalCalendar;
  disabled?: boolean;
  errorFlash?: number;
  onToggle: (selected: boolean) => void;
}

export function canUseCalendarForConflicts(calendar: ExternalCalendar): boolean {
  return calendar.capabilities.can_read_busy || calendar.capabilities.can_read_events;
}

const CalendarRow: React.FC<CalendarRowProps> = ({
  calendar,
  disabled = false,
  errorFlash,
  onToggle,
}) => {
  const canUseForConflicts = canUseCalendarForConflicts(calendar);
  const switchDisabled = disabled || !canUseForConflicts;
  const helperText = canUseForConflicts
    ? calendar.access_role
      ? `Access: ${calendar.access_role}`
      : undefined
    : 'This calendar cannot be used for conflict checks.';

  return (
    <Box
      data-testid="calendar-row"
      sx={(theme) => ({
        alignItems: 'center',
        animation: errorFlash ? 'calendar-settings-error-flash 900ms ease-in-out' : undefined,
        backgroundColor: errorFlash ? theme.palette.error.light : undefined,
        borderRadius: errorFlash ? 1 : undefined,
        display: 'flex',
        gap: 1.25,
        minHeight: 34,
        px: { xs: 1, sm: 3.75 },
        py: 0.75,
        transition: theme.transitions.create(['background-color'], {
          duration: theme.transitions.duration.short,
        }),
        '@keyframes calendar-settings-error-flash': {
          '0%': { backgroundColor: theme.palette.error.light },
          '100%': { backgroundColor: 'transparent' },
        },
      })}
    >
      <Tooltip
        title={
          switchDisabled && !canUseForConflicts
            ? 'Conflict checks require busy or event read access.'
            : ''
        }
      >
        <span>
          <Switch
            inputProps={{
              'aria-label': `Use ${calendar.summary || calendar.provider_calendar_id} for conflicts`,
            }}
            checked={calendar.selected_for_conflicts}
            disabled={switchDisabled}
            onChange={(_, checked) => onToggle(checked)}
            sx={{
              height: 24,
              p: 0,
              width: 40,
              '& .MuiSwitch-switchBase': {
                p: 0.5,
                transitionDuration: '180ms',
                '&.Mui-checked': {
                  transform: 'translateX(16px)',
                  '& .MuiSwitch-thumb': {
                    backgroundColor: '#637A6F',
                  },
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#637A6F',
                    opacity: 0.28,
                  },
                },
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: '#F4F4F4',
                boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                height: 16,
                width: 16,
              },
              '& .MuiSwitch-track': {
                backgroundColor: '#262626',
                borderRadius: 100,
                height: 10,
                margin: '7px',
                opacity: 0.38,
                width: 26,
              },
            }}
          />
        </span>
      </Tooltip>
      <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}>
        <Typography
          sx={{
            color: '#000000',
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: '0.15px',
            lineHeight: 1.5,
          }}
        >
          {calendar.summary || calendar.provider_calendar_id}
        </Typography>
        {calendar.primary ? <Chip label="Primary" size="small" variant="outlined" /> : null}
        {calendar.hidden ? (
          <Chip label="Hidden" size="small" color="warning" variant="outlined" />
        ) : null}
        {!canUseForConflicts ? <Chip label="Unsupported" size="small" variant="outlined" /> : null}
        {helperText ? (
          <Typography sx={{ color: '#A4A4A4', fontSize: 12, lineHeight: 1.5 }}>
            {helperText}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default CalendarRow;
